import React from 'react';
import PropTypes from 'prop-types';
import styled from 'libs/styled';
import { useTranslation } from 'react-i18next';
import getQueryParams from '@sportson/core-web/utils/getQueryParams';
import Link from '@sportson/core-web/components/Link';
import PaginationButton from 'components/Pagination/PaginationButton';
import TextStyles from 'components/Text';
import { useLocation } from 'react-router-dom';

const Wrapper = styled('div')`
    text-align: center;
    display: flex;
    justify-content: space-around;
    align-items: center;
    margin: auto;
    max-width: 452px;
    padding: 72px 0;
`;

const NumberButtonsWrapper = styled('div')`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
`;

const PageLink = styled(Link)`
    border: 1px solid ${(props) => (props.active ? 'var(--color-base-black)' : 'var(--color-neutrals-200)')};
    border-radius: 4px;
    display: initial;
    min-width: 32px;
    min-height: 32px;
    padding: 8px;
    background-color: ${(props) => (props.active ? 'var(--color-base-black)' : 'transparent')};
    color: ${(props) => (props.active ? 'var(--color-base-white)' : 'var(--color-base-black)')};

    word-wrap: nowrap;
`;

const DotElement = styled('div')`
    border: 1px solid transparent;
    display: initial;
    color: var(--color-base-black);
    min-width: 32px;
    min-height: 32px;
    padding: 8px;
`;

const Pagination = ({ page, pageCount, handleClick }) => {
    const { t } = useTranslation();
    const location = useLocation();

    const buttons = [];

    let min;
    let max;
    if (pageCount <= 4) {
        min = 0;
        max = pageCount;
    } else {
        min = page >= 3 ? page - 1 : 0;
        max = pageCount > min + 4 ? min + 4 : pageCount;
        if (page >= pageCount - 2) {
            min = pageCount - 4;
            max = pageCount;
        }
    }

    const getQueryUrl = (pagination) => {
        pagination += 1;
        const query = getQueryParams(location);

        if (pagination === 1) {
            delete query.page;
        } else {
            query.page = pagination;
        }
        delete query.category;

        return (
            location.pathname +
            (Object.entries(query).length
                ? `?${Object.entries(query)
                      .map((q) => q.join('='))
                      .join('&')}`
                : '')
        );
    };

    if (page >= 3 && pageCount > 4) {
        buttons.push(
            <PageLink
                data-is-first
                key="paginationStart"
                to={getQueryUrl(0)}
                m="0 8px 0 0"
                onClick={handleClick && handleClick.bind(this, 0)}
            >
                <TextStyles typography="UI/16_100_0_450">1</TextStyles>
            </PageLink>,
        );

        if (pageCount !== 5) {
            buttons.push(
                <DotElement key="empty1">
                    <TextStyles typography="UI/16_100_0_450">…</TextStyles>
                </DotElement>,
            );
        }
    }

    for (let index = min; index < max; index++) {
        buttons.push(
            <PageLink
                key={index}
                to={getQueryUrl(index)}
                active={page === index}
                onClick={page === index ? null : handleClick && handleClick.bind(this, index)}
            >
                <TextStyles typography="UI/16_100_0_450">{index + 1}</TextStyles>
            </PageLink>,
        );
    }
    if (pageCount > max) {
        if (pageCount !== 5) {
            buttons.push(
                <DotElement key="empty2">
                    <TextStyles typography="UI/16_100_0_450">…</TextStyles>
                </DotElement>,
            );
        }

        buttons.push(
            <PageLink
                data-is-last
                key="pageCount"
                to={getQueryUrl(pageCount - 1)}
                onClick={handleClick && handleClick.bind(this, pageCount - 1)}
            >
                <TextStyles typography="UI/16_100_0_450">{pageCount}</TextStyles>
            </PageLink>,
        );
    }

    return (
        <Wrapper>
            <PaginationButton
                active={page > 0}
                to={page > 0 ? getQueryUrl(page - 1) : getQueryUrl(page)}
                title={t('prev')}
                mr="8px"
                direction="left"
                onClick={handleClick && handleClick.bind(this, page - 1)}
            />
            <NumberButtonsWrapper>{buttons}</NumberButtonsWrapper>
            <PaginationButton
                active={page + 1 < pageCount}
                to={page + 1 < pageCount ? getQueryUrl(page + 1) : getQueryUrl(page)}
                title={t('next')}
                ml="8px"
                direction="right"
                onClick={handleClick && handleClick.bind(this, page + 1)}
            />
        </Wrapper>
    );
};

Pagination.propTypes = {
    handleClick: PropTypes.func,
    page: PropTypes.number.isRequired,
    pageCount: PropTypes.number.isRequired,
};

Pagination.defaultProps = {
    handleClick: () => null,
};

export default Pagination;
